import {
  SourcingAutomatedAction,
  SourcingStageStat,
} from 'api-clients/monolith';
import _round from 'lodash/round';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import { ConversionStats } from '@/components/ui/conversion-stats';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useOpeningDetails } from '@/contexts/opening-context';
import { durationInDays } from '@/utils/date-util';

import { AutomationActionPill } from './automation-action-pill';
import { HireStagesHoverCard } from './hire-stages-hover-card';

interface StageProps {
  stage: SourcingStageStat;
  automatedAction: SourcingAutomatedAction | null;
}

export function Stage({ stage, automatedAction }: StageProps) {
  const { hiringGoal } = useOpeningDetails();

  const {
    title: sourceStage,
    hire_stage_title: hireStage,
    applicants_stats: applicantStats,
    performance_status: status,
    stage_type: stageType,
    conversion_stats: conversionStats,
    applicants_hired_in_stages: applicantsHiredInStages,
  } = stage;
  const { expected, actual } = applicantStats;

  const expectedByToday = useMemo(() => {
    if (!hiringGoal || !expected) return 0;

    const perDayExpected = expected / hiringGoal.duration;
    const daysElapsed = durationInDays(hiringGoal.start_date, new Date());

    return _round(perDayExpected * daysElapsed);
  }, [hiringGoal, expected]);

  return (
    <>
      <div className="bg-fx-neutral-200 h-px w-full" />

      <div className="flex justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <Typography variant="text">{sourceStage}</Typography>
            {stageType === 'hired' && applicantsHiredInStages.length > 0 ? (
              <HireStagesHoverCard
                applicantsHiredInStages={applicantsHiredInStages}
              />
            ) : (
              <Typography variant="body1" className="!text-fx-neutral-400">
                {`(${hireStage})`}
              </Typography>
            )}
          </div>

          <Typography variant="small1" className="!text-fx-neutral-600">
            {`${actual ?? 0}${
              hiringGoal && hiringGoal?.external_id
                ? ` / ${expected ?? '-'}`
                : ''
            } applicants`}
          </Typography>

          {stageType !== 'hired' &&
            hiringGoal?.is_current &&
            hiringGoal?.external_id && (
              <div className="flex">
                <Typography
                  variant="small1"
                  className="!text-fx-neutral-600 mr-1"
                >
                  <FormattedMessage
                    defaultMessage="Conversion to hire:"
                    description="Label before forecast applicant-to-hire % for the stage"
                  />
                </Typography>
                <ConversionStats
                  stats={conversionStats}
                  typographyVariant="small1"
                  className="!text-fx-neutral-600"
                />
              </div>
            )}
        </div>

        {hiringGoal?.is_current && (
          <PerformanceStatusPill
            status={status}
            criteria="apply"
            expected={expectedByToday}
            actual={actual}
          />
        )}
        {!hiringGoal && (
          <PerformanceStatusPill
            status={status}
            criteria="apply"
            expected={expectedByToday}
            actual={actual}
          />
        )}
      </div>

      {automatedAction && automatedAction.stage_type === stageType && (
        <AutomationActionPill action={automatedAction.action} />
      )}
    </>
  );
}

Stage.Skeleton = function StageSkeleton() {
  return (
    <>
      <div className="bg-fx-neutral-200 h-px w-full" />
      <div className="flex justify-between">
        <div className="flex flex-col gap-3">
          <div className="flex h-5 items-center gap-1">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-15" />
          </div>
          <Skeleton className="h-3 w-30" />
          <Skeleton className="h-3 w-50" />
        </div>
        <PerformanceStatusPill.Skeleton />
      </div>
    </>
  );
};
